/**
 * ComponentRegistry - Component registration and instance management
 *
 * Central registry for all component types and instances. Handles component
 * registration, DOM scanning, initialization, and instance lifecycle management.
 *
 * Based on design document section 3.1 (Component Registry).
 */

import type {
  ComponentRegistry as IComponentRegistry,
  ComponentHandler,
  ComponentInstance,
} from '../types/index.js';

/**
 * Configuration for ComponentRegistry
 */
interface ComponentRegistryConfig {
  /** Enable debug logging */
  debug: boolean;
  /** Attribute name for component type */
  componentAttribute: string;
  /** Attribute name for component config */
  configAttribute: string;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: ComponentRegistryConfig = {
  debug: false,
  componentAttribute: 'data-dr-component',
  configAttribute: 'data-dr-config',
};

/**
 * ComponentRegistry implementation
 */
export class ComponentRegistry implements IComponentRegistry {
  private config: ComponentRegistryConfig;
  private handlers: Map<string, ComponentHandler> = new Map();
  private instances: Map<string, ComponentInstance> = new Map();
  private instanceCounter = 0;
  private isInitialized = false;

  constructor(config: Partial<ComponentRegistryConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Register a component handler
   */
  register(type: string, handler: ComponentHandler): void {
    try {
      if (!type || typeof type !== 'string') {
        throw new Error('Component type must be a non-empty string');
      }

      if (!handler || typeof handler !== 'object') {
        throw new Error('Component handler must be an object');
      }

      if (typeof handler.init !== 'function') {
        throw new Error('Component handler must have an init method');
      }

      if (!handler.defaultConfig || typeof handler.defaultConfig !== 'object') {
        throw new Error('Component handler must have a defaultConfig object');
      }

      this.handlers.set(type, handler);
      this.log(`Registered component handler: ${type}`);
    } catch (error) {
      this.handleError('Error registering component handler', error);
      throw error;
    }
  }

  /**
   * Unregister a component handler
   */
  unregister(type: string): void {
    try {
      if (!this.handlers.has(type)) {
        this.log(`Component type ${type} is not registered`);
        return;
      }

      // Destroy all instances of this type
      const instances = this.getInstances(type);
      instances.forEach((instance) => {
        this.destroyInstance(instance.id);
      });

      // Remove handler
      this.handlers.delete(type);
      this.log(`Unregistered component handler: ${type}`);
    } catch (error) {
      this.handleError('Error unregistering component handler', error);
    }
  }

  /**
   * Get handler for component type
   */
  getHandler(type: string): ComponentHandler | undefined {
    return this.handlers.get(type);
  }

  /**
   * Scan DOM and initialize all components
   */
  scanAndInitialize(): void {
    try {
      this.log('Scanning DOM for components...');

      // Find all elements with component attribute
      const elements = document.querySelectorAll(`[${this.config.componentAttribute}]`);
      let initializedCount = 0;

      elements.forEach((element) => {
        const type = element.getAttribute(this.config.componentAttribute);
        if (!type) {
          this.log('Element has component attribute but no type value');
          return;
        }

        // Check if handler is registered
        const handler = this.handlers.get(type);
        if (!handler) {
          this.log(`No handler registered for component type: ${type}`);
          return;
        }

        // Check if element already has a component instance
        const existingInstance = this.findInstanceByElement(element as HTMLElement);
        if (existingInstance) {
          this.log(`Element already has component instance: ${existingInstance.id}`);
          return;
        }

        // Parse configuration
        const configAttr = element.getAttribute(this.config.configAttribute);
        let userConfig: Record<string, any> = {};

        if (configAttr) {
          try {
            userConfig = JSON.parse(configAttr);
          } catch (parseError) {
            this.handleError('Error parsing component config', parseError);
            // Continue with empty config
          }
        }

        // Merge with default config
        const config = { ...handler.defaultConfig, ...userConfig };

        try {
          // Initialize component
          const instance = handler.init(element as HTMLElement, config);

          // Validate instance
          if (!this.validateInstance(instance)) {
            throw new Error('Component handler returned invalid instance');
          }

          // Generate unique ID if not provided
          if (!instance.id) {
            instance.id = this.generateInstanceId(type);
          }

          // Set type if not provided
          if (!instance.type) {
            instance.type = type;
          }

          // Store instance
          this.instances.set(instance.id, instance);
          initializedCount++;

          this.log(`Initialized component: ${type} (${instance.id})`);
        } catch (initError) {
          this.handleError('Error initializing component', initError);
        }
      });

      this.isInitialized = true;
      this.log(`Component scan complete. Initialized ${initializedCount} components.`);
    } catch (error) {
      this.handleError('Error during component scan and initialization', error);
    }
  }

  /**
   * Get all component instances, optionally filtered by type
   */
  getInstances(type?: string): ComponentInstance[] {
    const instances = Array.from(this.instances.values());
    return type ? instances.filter((instance) => instance.type === type) : instances;
  }

  /**
   * Destroy a component instance
   */
  destroyInstance(id: string): void {
    try {
      const instance = this.instances.get(id);
      if (!instance) {
        this.log(`Component instance not found: ${id}`);
        return;
      }

      // Call destroy method if available
      if (typeof instance.destroy === 'function') {
        try {
          instance.destroy();
        } catch (destroyError) {
          this.handleError('Error calling component destroy method', destroyError);
        }
      }

      // Remove from registry
      this.instances.delete(id);
      this.log(`Destroyed component instance: ${id}`);
    } catch (error) {
      this.handleError('Error destroying component instance', error);
    }
  }

  /**
   * Find component instance by DOM element
   */
  findInstanceByElement(element: HTMLElement): ComponentInstance | undefined {
    return Array.from(this.instances.values()).find((instance) => instance.element === element);
  }

  /**
   * Get component instance by ID
   */
  getInstance(id: string): ComponentInstance | undefined {
    return this.instances.get(id);
  }

  /**
   * Get all registered component types
   */
  getRegisteredTypes(): string[] {
    return Array.from(this.handlers.keys());
  }

  /**
   * Check if component type is registered
   */
  isTypeRegistered(type: string): boolean {
    return this.handlers.has(type);
  }

  /**
   * Get total number of component instances
   */
  getInstanceCount(): number {
    return this.instances.size;
  }

  /**
   * Get total number of registered handlers
   */
  getHandlerCount(): number {
    return this.handlers.size;
  }

  /**
   * Reinitialize all components (useful after DOM changes)
   */
  reinitialize(): void {
    this.log('Reinitializing component registry...');

    // Destroy all existing instances
    const instanceIds = Array.from(this.instances.keys());
    instanceIds.forEach((id) => {
      this.destroyInstance(id);
    });

    // Reset state
    this.isInitialized = false;
    this.instanceCounter = 0;

    // Scan and initialize again
    this.scanAndInitialize();
  }

  /**
   * Destroy all components and clear registry
   */
  destroy(): void {
    this.log('Destroying component registry...');

    // Destroy all instances
    const instanceIds = Array.from(this.instances.keys());
    instanceIds.forEach((id) => {
      this.destroyInstance(id);
    });

    // Clear handlers
    this.handlers.clear();

    // Reset state
    this.isInitialized = false;
    this.instanceCounter = 0;

    this.log('Component registry destroyed');
  }

  /**
   * Generate unique instance ID
   */
  private generateInstanceId(type: string): string {
    return `${type}-${this.instanceCounter++}`;
  }

  /**
   * Validate component instance
   */
  private validateInstance(instance: any): instance is ComponentInstance {
    if (!instance || typeof instance !== 'object') {
      return false;
    }

    // Check required properties
    if (!instance.element || !(instance.element instanceof HTMLElement)) {
      return false;
    }

    if (!instance.config || typeof instance.config !== 'object') {
      return false;
    }

    // Check required methods
    if (typeof instance.update !== 'function') {
      return false;
    }

    if (typeof instance.destroy !== 'function') {
      return false;
    }

    return true;
  }

  /**
   * Handle errors and log them
   */
  private handleError(message: string, error: unknown): void {
    console.error(`ComponentRegistry: ${message}`, error);

    // Could send error message to parent via MessageHandler if available
    // For now, just log to console
  }

  /**
   * Debug logging
   */
  private log(message: string, ...args: unknown[]): void {
    if (this.config.debug) {
      // eslint-disable-next-line no-console
      console.log(`[ComponentRegistry] ${message}`, ...args);
    }
  }

  /**
   * Enable debug mode
   */
  enableDebug(): void {
    this.config.debug = true;
    this.log('Debug mode enabled');
  }

  /**
   * Disable debug mode
   */
  disableDebug(): void {
    this.config.debug = false;
  }

  /**
   * Get current configuration
   */
  getConfig(): ComponentRegistryConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<ComponentRegistryConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.log('Configuration updated:', this.config);
  }

  /**
   * Check if registry is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get registry statistics
   */
  getStats(): {
    handlersCount: number;
    instancesCount: number;
    instancesByType: Record<string, number>;
    isInitialized: boolean;
  } {
    const instancesByType: Record<string, number> = {};

    // Count instances by type
    this.instances.forEach((instance) => {
      instancesByType[instance.type] = (instancesByType[instance.type] || 0) + 1;
    });

    return {
      handlersCount: this.handlers.size,
      instancesCount: this.instances.size,
      instancesByType,
      isInitialized: this.isInitialized,
    };
  }
}